/**
 * Validation > 2nd line support — the Veeva user manager (ADR-0038).
 *
 * One Veeva connection (picked in the header, only ones the caller may use)
 * drives four tabs: the **Assistant** (an AI agent that looks users up and
 * proposes changes), **Users**, **Create user** and **Update user** (plain
 * forms calling the API directly). Every create / update — from the agent or
 * a form — is a proposal the user confirms; sensitive fields also need an
 * Administrator's approval. Nothing is written to Veeva without that.
 */

import { useEffect, useState } from "react";
import { Alert, Anchor, Group, Loader, Select, Stack, Tabs, Text, Title } from "@mantine/core";
import {
  IconAlertTriangle,
  IconMessageChatbot,
  IconUserEdit,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  getPicklists,
  listVeevaConnections,
  type PicklistsOut,
  type VeevaConnectionOut,
} from "@/shared/api/userAdmin";
import { AgentChat } from "./AgentChat";
import { CreateUserForm } from "./CreateUserForm";
import { UpdateUserForm } from "./UpdateUserForm";
import { UsersPanel } from "./UsersPanel";

const CONNECTION_KEY = "daquma.second-line.veeva-connection";

function remembered(): string | null {
  try {
    return window.localStorage.getItem(CONNECTION_KEY);
  } catch {
    return null;
  }
}

function remember(id: string): void {
  try {
    window.localStorage.setItem(CONNECTION_KEY, id);
  } catch {
    /* per-viewer convenience only */
  }
}

export function SecondLineSupport() {
  const { chatId = null } = useParams();
  const getToken = useAccessToken();
  const [connections, setConnections] = useState<VeevaConnectionOut[] | null>(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [lists, setLists] = useState<PicklistsOut | null>(null);
  const [listsError, setListsError] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>("assistant");

  useEffect(() => {
    void (async () => {
      try {
        const out = await listVeevaConnections(await getToken());
        setConnections(out.items);
        const keep = remembered();
        const first = out.items.find((c) => c.connection_id === keep) ?? out.items[0];
        setConnectionId(first?.connection_id ?? null);
      } catch {
        setConnections([]);
      }
    })();
  }, [getToken]);

  useEffect(() => {
    if (!connectionId) return;
    setLists(null);
    setListsError(null);
    let live = true;
    void (async () => {
      try {
        const out = await getPicklists(await getToken(), connectionId);
        if (live) setLists(out);
      } catch (err) {
        if (live) setListsError(err instanceof Error ? err.message : "Could not reach Veeva.");
      }
    })();
    return () => {
      live = false;
    };
  }, [connectionId, getToken]);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={2}>
          <Title order={2}>2nd line support</Title>
          <Text size="sm" c="dimmed">
            Manage Veeva Vault users — with the assistant or the forms.
          </Text>
        </Stack>
        {connections === null ? (
          <Loader size="xs" />
        ) : (
          <Select
            label="Veeva connection"
            data={connections.map((c) => ({ value: c.connection_id, label: c.name }))}
            value={connectionId}
            onChange={(v) => {
              setConnectionId(v);
              if (v) remember(v);
            }}
            allowDeselect={false}
            w={280}
            data-testid="veeva-connection"
          />
        )}
      </Group>
      <Alert
        color="yellow"
        variant="light"
        icon={<IconAlertTriangle size={16} />}
        data-testid="advisory-banner"
      >
        The assistant only prepares changes. Nothing changes in Veeva until you confirm, and
        security profile, license, activation, domain-admin and security-policy changes also need an
        administrator&apos;s approval. Review every proposal.
      </Alert>
      {connections !== null && connections.length === 0 && (
        <Alert color="yellow" variant="light" title="No Veeva connection">
          You need a Veeva connection you may use. Add one under{" "}
          <Anchor component={RouterLink} to="/integration/connections/new">
            Integrations → Add connection
          </Anchor>
          , or ask its owner to share it with you.
        </Alert>
      )}
      {listsError && (
        <Alert color="red" variant="light" title="Veeva did not answer">
          {listsError}
        </Alert>
      )}
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="assistant" leftSection={<IconMessageChatbot size={16} />}>
            Assistant
          </Tabs.Tab>
          <Tabs.Tab value="users" leftSection={<IconUsers size={16} />} disabled={!connectionId}>
            Users
          </Tabs.Tab>
          <Tabs.Tab
            value="create"
            leftSection={<IconUserPlus size={16} />}
            disabled={!connectionId}
          >
            Create user
          </Tabs.Tab>
          <Tabs.Tab
            value="update"
            leftSection={<IconUserEdit size={16} />}
            disabled={!connectionId}
          >
            Update user
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="assistant" pt="md">
          <AgentChat chatId={chatId} connectionId={connectionId} />
        </Tabs.Panel>
        {connectionId && (
          <>
            <Tabs.Panel value="users" pt="md">
              <UsersPanel key={connectionId} connectionId={connectionId} />
            </Tabs.Panel>
            <Tabs.Panel value="create" pt="md">
              <CreateUserForm key={connectionId} connectionId={connectionId} lists={lists} />
            </Tabs.Panel>
            <Tabs.Panel value="update" pt="md">
              <UpdateUserForm key={connectionId} connectionId={connectionId} lists={lists} />
            </Tabs.Panel>
          </>
        )}
      </Tabs>
    </Stack>
  );
}
